/**
 * Versões em markdown das páginas públicas (os "gêmeos" `/{caminho}.md`) e o
 * `llms.txt`. Puro, sem React e sem disco: quem usa é
 * - `scripts/build-agent-layer.mjs`, que grava `dist/**\/*.md` e `dist/llms.txt`
 *   depois do `expo export`;
 * - `middleware.ts` (Routing Middleware da Vercel), que responde
 *   `Accept: text/markdown` na URL da página com o mesmo texto;
 * - os testes de SEO.
 *
 * Fonte: os registros de `content/seo/*`, `content/marketing/home.ts` e
 * `content/facts.ts`. Os registros mudam de forma com o tempo, então os
 * geradores leem os campos que conhecem e ignoram o resto, sem quebrar.
 *
 * A canônica sai do próprio documento (`source_url`), nunca do caminho do
 * pedido; a da home é `/`, não `/index`.
 */
import { COMPANY, FACTS_CHECKED_AT, PAYMENT_FEES, PROSE, formatBRL } from '../../content/facts';
import {
  BRAND_DESCRIPTION,
  BRAND_TITLE,
  FEATURES,
  FEATURES_SECTION,
  HOME_FAQ,
  HOME_HERO,
  HOW_IT_WORKS,
  HOW_IT_WORKS_SECTION,
  PANEL_SECTION,
  PLANS_SECTION,
  planComparisonTable,
} from '../../content/marketing/home';
import {
  FIXED_PAGES,
  FOR_END_CLIENT,
  FREE_AND_PAID,
  LLMS_SUMMARY,
  LLMS_TITLE,
  WHEN_TO_USE,
  WHEN_TO_USE_HEADING,
  agentUsageLines,
  paymentBlock,
  plansBlock,
} from '../../content/marketing/llms';
import { ABOUT_ANSWER, ABOUT_DESCRIPTION, ABOUT_FACTS, ABOUT_PATH, ABOUT_SECTIONS, ABOUT_TITLE } from '../../content/legal/about';
import {
  CONTACT_ANSWER,
  CONTACT_DESCRIPTION,
  CONTACT_PATH,
  CONTACT_SOCIAL,
  CONTACT_SUBJECTS,
  CONTACT_TITLE,
} from '../../content/legal/contact';
import {
  LAW_LINKS,
  LEGAL_DISCLAIMER as PRIVACY_DISCLAIMER,
  PRIVACY_DESCRIPTION,
  PRIVACY_PATH,
  PRIVACY_SECTIONS,
  PRIVACY_TITLE,
  PRIVACY_VERSION,
  PRIVACY_VERSION_LABEL,
} from '../../content/legal/privacy';
import { COMPARISON_PAGES } from '../../content/seo/comparisons';
import * as COMPARISONS_MODULE from '../../content/seo/comparisons';
import { GUIDE_PAGES } from '../../content/seo/guides';
import * as GUIDES_MODULE from '../../content/seo/guides';
import { SEGMENT_PAGES } from '../../content/seo/segments';

export const SITE_URL = 'https://www.controlapag.com.br';

/** Limite do mapa (`llms.txt`): os verificadores apontam acima de ~30.000. */
export const LLMS_MAX_CHARS = 30_000;

type Rec = Record<string, unknown>;

const isRec = (value: unknown): value is Rec => typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
const strs = (value: unknown): string[] => (Array.isArray(value) ? value.map(str).filter((v): v is string => !!v) : []);
const recs = (value: unknown): Rec[] => (Array.isArray(value) ? value.filter(isRec) : []);

/** Caminho do gêmeo: `/` vira `/index.md`, o resto ganha `.md`. */
export const mdPathFor = (path: string): string => (path === '/' ? '/index.md' : `${path}.md`);
export const absolute = (path: string): string => (path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`);

function frontMatter(fields: { title: string; path: string; description?: string; updated?: string }): string {
  const clean = (text: string) => text.replace(/\s+/g, ' ').trim();
  const lines = [
    `title: ${clean(fields.title)}`,
    `source_url: ${absolute(fields.path)}`,
    ...(fields.updated ? [`updated: ${fields.updated}`] : []),
    ...(fields.description ? [`description: ${clean(fields.description)}`] : []),
    'language: pt-BR',
  ];
  return `---\n${lines.join('\n')}\n---\n\n`;
}

/** Lê `source_url:` do front matter (vira o `Link: rel="canonical"`). */
export function sourceUrlOf(doc: string): string | null {
  return /^source_url:\s*(\S+)\s*$/m.exec(doc)?.[1] ?? null;
}

const escapeCell = (text: string) => text.replace(/\|/g, '\\|').replace(/\n+/g, ' ');

function table(columns: string[], rows: string[][]): string[] {
  return [
    `| ${columns.map(escapeCell).join(' | ')} |`,
    `| ${columns.map(() => '---').join(' | ')} |`,
    ...rows.map((row) => `| ${row.map(escapeCell).join(' | ')} |`),
    '',
  ];
}

const list = (items: string[]) => (items.length ? [...items.map((item) => `- ${item}`), ''] : []);
const numbered = (items: string[]) => (items.length ? [...items.map((item, i) => `${i + 1}. ${item}`), ''] : []);

/** Uma seção genérica: pergunta, cápsula, parágrafos, passos, modelo, tabela e fontes. */
function section(sec: Rec, level = 2): string[] {
  const heading = str(sec.heading) ?? str(sec.title) ?? str(sec.question);
  if (!heading) return [];
  const out = [`${'#'.repeat(level)} ${heading}`, ''];
  for (const key of ['capsule', 'answer', 'body', 'description']) {
    const text = str(sec[key]);
    if (text) out.push(text, '');
  }
  for (const p of [...strs(sec.body), ...strs(sec.paragraphs)]) out.push(p, '');
  out.push(...numbered(strs(sec.steps)));
  out.push(...list(strs(sec.items)));
  const links = recs(sec.links).filter((l) => str(l.label) && str(l.url));
  if (links.length) {
    out.push(...list(links.map((l) => `[${str(l.label)}](${String(l.url).startsWith('/') ? SITE_URL + String(l.url) : l.url})`)));
  }
  const template = str(sec.template);
  if (template) out.push('Mensagem pronta:', '', ...template.split('\n').map((line) => `> ${line}`), '');
  const columns = strs(sec.columns);
  const rows = Array.isArray(sec.rows) ? (sec.rows as unknown[]).filter(Array.isArray).map((r) => (r as unknown[]).map(String)) : [];
  if (columns.length && rows.length) out.push(...table(columns, rows));
  const sources = recs(sec.legalSources ?? sec.sources);
  if (sources.length) {
    out.push(
      'Fontes:',
      '',
      ...list(
        sources.map((s) => {
          const label = [str(s.law) ?? str(s.label), str(s.article)].filter(Boolean).join(', ');
          return str(s.url) ? `[${label}](${s.url})` : label;
        }),
      ),
    );
  }
  return out;
}

function faqBlock(items: unknown, heading = 'Perguntas frequentes'): string[] {
  const faq = recs(items).filter((item) => str(item.question) && str(item.answer));
  if (!faq.length) return [];
  return [`## ${heading}`, '', ...faq.flatMap((item) => [`### ${str(item.question)}`, '', str(item.answer)!, ''])];
}

const link = (label: string, path: string) => `[${label}](${SITE_URL}${mdPathFor(path)})`;

function finish(lines: string[]): string {
  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()}\n`;
}

/* -------------------------------------------------------------------------- */
/* Registros                                                                  */
/* -------------------------------------------------------------------------- */

const guideQuestion = (g: Rec): string => {
  const candidates = [str(g.question), str(g.h1), str(g.title)].filter((v): v is string => !!v);
  return candidates.find((c) => c.endsWith('?')) ?? candidates[0] ?? '';
};

export function guideMarkdown(slug: string): string | null {
  const g = (GUIDE_PAGES as unknown as Rec[]).find((page) => page.slug === slug);
  if (!g) return null;
  const path = `/guias/${slug}`;
  const product = isRec(g.product) ? g.product : null;
  const productHeading = str((GUIDES_MODULE as Rec).GUIDE_PRODUCT_HEADING) ?? 'Como a Controlapag faz isso?';
  const lines = [
    frontMatter({ title: str(g.title) ?? slug, path, description: str(g.description), updated: str(g.updatedAt) }),
    `# ${str(g.h1) ?? str(g.title)}`,
    '',
    str(g.answer) ?? '',
    '',
    ...recs(g.sections).flatMap((sec) => section(sec)),
    ...(product
      ? [
          `## ${productHeading}`,
          '',
          str(product.capsule) ?? '',
          '',
          ...numbered(strs(product.steps)),
          str(product.paidNote) ?? '',
          '',
        ]
      : []),
    ...faqBlock(g.faq),
    ...relatedLinks(g),
    `Publicado em ${str(g.publishedAt) ?? '-'}; revisado em ${str(g.updatedAt) ?? '-'}. Autor: Equipe Controlapag.`,
  ];
  return finish(lines);
}

function relatedLinks(page: Rec): string[] {
  const guides = strs(page.related)
    .map((slug) => (GUIDE_PAGES as unknown as Rec[]).find((g) => g.slug === slug))
    .filter(isRec)
    .map((g) => link(guideQuestion(g), `/guias/${g.slug}`));
  const segments = strs(page.segments)
    .map((slug) => (SEGMENT_PAGES as unknown as Rec[]).find((s) => s.slug === slug))
    .filter(isRec)
    .map((s) => link(str(s.label) ?? String(s.slug), `/para/${s.slug}`));
  const items = [...guides, ...segments];
  return items.length ? ['## Leia também', '', ...list(items)] : [];
}

/** Campos de ramo que não são texto da página (prints de exemplo, ícones). */
const SEGMENT_SKIP = new Set(['slug', 'label', 'title', 'description', 'h1', 'sub', 'clientWord', 'tagline', 'mock', 'related', 'faq', 'pains', 'features']);

export function segmentMarkdown(slug: string): string | null {
  const s = (SEGMENT_PAGES as unknown as Rec[]).find((page) => page.slug === slug);
  if (!s) return null;
  const path = `/para/${slug}`;
  const pains = recs(s.pains).map((p) => [str(p.label), str(p.description) ?? str(p.detail)].filter(Boolean).join(': '));
  const features = recs(s.features).map((f) =>
    [str(f.title), str(f.description) ?? str(f.short)].filter(Boolean).join(': '),
  );
  // Seções novas que a área de ramos acrescentar (lista de objetos com `heading`).
  const extra = Object.entries(s)
    .filter(([key]) => !SEGMENT_SKIP.has(key))
    .flatMap(([, value]) => recs(value).filter((v) => str(v.heading)).flatMap((sec) => section(sec)));
  const related = strs(s.related)
    .map((r) => (SEGMENT_PAGES as unknown as Rec[]).find((p) => p.slug === r))
    .filter(isRec)
    .map((p) => link(str(p.label) ?? String(p.slug), `/para/${p.slug}`));
  const guides = (GUIDE_PAGES as unknown as Rec[])
    .filter((g) => strs(g.segments).includes(slug))
    .map((g) => link(guideQuestion(g), `/guias/${g.slug}`));
  const lines = [
    frontMatter({ title: str(s.title) ?? slug, path, description: str(s.description) }),
    `# ${str(s.h1) ?? str(s.title)}`,
    '',
    str(s.answer) ?? str(s.sub) ?? '',
    '',
    ...(pains.length ? ['## O que dá errado hoje?', '', ...list(pains)] : []),
    ...(features.length ? ['## O que a Controlapag faz por esse ramo?', '', ...list(features)] : []),
    ...extra,
    ...faqBlock(s.faq),
    ...(guides.length ? ['## Guias para esse ramo', '', ...list(guides)] : []),
    ...(related.length ? ['## Outros ramos', '', ...list(related)] : []),
    `Planos: ${PROSE.free}; planos pagos ${PROSE.fromPrice} no site. Conferido em ${FACTS_CHECKED_AT}.`,
  ];
  return finish(lines);
}

export function comparisonMarkdown(slug: string): string | null {
  const c = (COMPARISON_PAGES as unknown as Rec[]).find((page) => page.slug === slug);
  if (!c) return null;
  const path = `/comparar/${slug}`;
  const other = str(c.other) ?? 'o outro jeito';
  const otherWith = str(c.otherWithArticle) ?? other.toLowerCase();
  // Mesma regra de `comparisonRows`/`usLabelOf` (components/seo/comparison-table.tsx),
  // repetida aqui porque aquele arquivo puxa React Native.
  const usLabel = str(c.usLabel) ?? 'Controlapag';
  const rows = recs(c.rows).map((r) => [
    str(r.criterion) ?? '',
    r.paid ? `${str(r.us) ?? ''} (planos pagos)` : (str(r.us) ?? ''),
    str(r.them) ?? '',
  ]);
  const freeVsPaid = str((COMPARISONS_MODULE as Rec).FREE_VS_PAID);
  const usTitle = str(c.chooseUsTitle) ?? 'Escolha a Controlapag se';
  const themTitle =
    str(c.chooseThemTitle) ?? (c.kind === 'manual' ? `Fique com ${otherWith} se` : `Escolha ${otherWith} se`);
  const sources = recs(c.sources).map((s) => (str(s.url) ? `[${str(s.label) ?? s.url}](${s.url})` : (str(s.label) ?? '')));
  const lines = [
    frontMatter({ title: str(c.title) ?? slug, path, description: str(c.description), updated: str(c.checkedAt) }),
    `# ${str(c.h1) ?? str(c.title)}`,
    '',
    str(c.answer) ?? '',
    '',
    ...(rows.length
      ? [`## ${usLabel} ou ${otherWith}: o que muda?`, '', ...table(['Critério', usLabel, other], rows), ...(freeVsPaid ? [freeVsPaid, ''] : [])]
      : []),
    ...recs(c.sections).flatMap((sec) => section(sec)),
    ...(strs(c.chooseUs).length ? [`## ${usTitle}`, '', ...list(strs(c.chooseUs))] : []),
    ...(strs(c.chooseThem).length ? [`## ${themTitle}`, '', ...list(strs(c.chooseThem))] : []),
    ...faqBlock(c.faq),
    ...relatedLinks(c),
    ...(sources.length ? ['## Fontes', '', ...list(sources)] : []),
    ...(str(c.checkedAt) ? [`Conferido em ${str(c.checkedAt)}.`] : []),
  ];
  return finish(lines);
}

/* -------------------------------------------------------------------------- */
/* Home, hub e institucionais                                                 */
/* -------------------------------------------------------------------------- */

export function homeMarkdown(): string {
  const plans = planComparisonTable();
  const lines = [
    frontMatter({ title: BRAND_TITLE, path: '/', description: BRAND_DESCRIPTION, updated: FACTS_CHECKED_AT }),
    `# ${HOME_HERO.h1}`,
    '',
    HOME_HERO.answer,
    '',
    `## ${FEATURES_SECTION.heading}`,
    '',
    FEATURES_SECTION.capsule,
    '',
    ...list(FEATURES.map((f) => `**${f.title}**: ${f.description}`)),
    `## ${PANEL_SECTION.heading}`,
    '',
    PANEL_SECTION.capsule,
    '',
    `## ${HOW_IT_WORKS_SECTION.heading}`,
    '',
    HOW_IT_WORKS_SECTION.capsule,
    '',
    ...numbered(HOW_IT_WORKS.map((step) => `**${step.title}**: ${step.description}`)),
    `## ${PLANS_SECTION.heading}`,
    '',
    PLANS_SECTION.capsule,
    '',
    ...table(
      plans.columns.map((col) => col.label),
      plans.rows.map((row) => row.map(String)),
    ),
    `Preço mensal no site, conferido em ${FACTS_CHECKED_AT}. Taxas do recebimento pelo link: ${link('Receber pela Controlapag', '/ajuda/recebimentos')}.`,
    '',
    ...faqBlock(HOME_FAQ),
    '## Para onde ir depois',
    '',
    ...list([
      link('Guias para autônomo', '/guias'),
      ...(SEGMENT_PAGES as unknown as Rec[]).map((s) => link(str(s.label) ?? String(s.slug), `/para/${s.slug}`)),
      `[Criar conta grátis](${SITE_URL}/register)`,
    ]),
  ];
  return finish(lines);
}

export function guidesHubMarkdown(): string {
  const moments = recs((GUIDES_MODULE as Rec).GUIDE_MOMENTS);
  const guides = GUIDE_PAGES as unknown as Rec[];
  const line = (g: Rec) => `${link(guideQuestion(g), `/guias/${g.slug}`)}${str(g.oneLine) ? `: ${str(g.oneLine)}` : ''}`;
  const grouped = moments.length
    ? moments.flatMap((m) => {
        const items = guides.filter((g) => g.moment === m.id);
        return items.length ? [`## ${str(m.label)}`, '', ...list(items.map(line))] : [];
      })
    : [];
  const ungrouped = guides.filter((g) => !moments.some((m) => m.id === g.moment));
  const hubTitle = str((GUIDES_MODULE as Rec).GUIDES_HUB_TITLE) ?? 'Guias para autônomo';
  const hubDescription =
    str((GUIDES_MODULE as Rec).GUIDES_HUB_DESCRIPTION) ??
    'Respostas práticas para quem atende cliente: falta, cancelamento, mensalidade atrasada, lembrete e preço da hora.';
  const lines = [
    frontMatter({ title: hubTitle, path: '/guias', description: hubDescription }),
    `# ${hubTitle}`,
    '',
    'Guias da Controlapag para o dia a dia de quem atende cliente com hora marcada ou mensalidade, escritos pela Equipe Controlapag.',
    '',
    ...grouped,
    ...(ungrouped.length ? ['## Outros guias', '', ...list(ungrouped.map(line))] : []),
    '## Comparações',
    '',
    ...list(
      (COMPARISON_PAGES as unknown as Rec[]).map(
        (c) => `${link(str(c.h1) ?? str(c.title) ?? String(c.slug), `/comparar/${c.slug}`)}`,
      ),
    ),
  ];
  return finish(lines);
}

export function aboutMarkdown(): string {
  const lines = [
    frontMatter({ title: ABOUT_TITLE, path: ABOUT_PATH, description: ABOUT_DESCRIPTION, updated: FACTS_CHECKED_AT }),
    `# ${ABOUT_TITLE}`,
    '',
    ABOUT_ANSWER,
    '',
    ...(ABOUT_SECTIONS as unknown as Rec[]).flatMap((sec) => [
      ...section(sec),
      ...(sec.id === 'para-quem'
        ? list((SEGMENT_PAGES as unknown as Rec[]).map((s) => link(str(s.label) ?? String(s.slug), `/para/${s.slug}`)))
        : []),
    ]),
    '## A empresa em resumo',
    '',
    ...table(
      ['Campo', 'Valor'],
      (ABOUT_FACTS as unknown as Rec[]).map((f) => [str(f.label) ?? '', str(f.url) ? `[${str(f.value)}](${f.url})` : (str(f.value) ?? '')]),
    ),
  ];
  return finish(lines);
}

export function contactMarkdown(): string {
  const lines = [
    frontMatter({ title: CONTACT_TITLE, path: CONTACT_PATH, description: CONTACT_DESCRIPTION, updated: FACTS_CHECKED_AT }),
    `# ${CONTACT_TITLE}`,
    '',
    CONTACT_ANSWER,
    '',
    '## Sobre o que escrever?',
    '',
    ...(CONTACT_SUBJECTS as unknown as Rec[]).flatMap((subject) => [
      `### ${str(subject.title)}`,
      '',
      `Quando: ${str(subject.when) ?? ''}`,
      '',
      `O que mandar: ${str(subject.send) ?? ''}`,
      '',
      `Assunto do e-mail: "${str(subject.emailSubject) ?? ''}", para ${COMPANY.email}.`,
      '',
    ]),
    '## Redes',
    '',
    ...list((CONTACT_SOCIAL as unknown as Rec[]).map((s) => `[${str(s.label)}](${s.url})`)),
  ];
  return finish(lines);
}

export function privacyMarkdown(): string {
  const lines = [
    frontMatter({ title: PRIVACY_TITLE, path: PRIVACY_PATH, description: PRIVACY_DESCRIPTION, updated: PRIVACY_VERSION }),
    `# ${PRIVACY_TITLE}`,
    '',
    `Versão de ${PRIVACY_VERSION_LABEL}.`,
    '',
    ...(PRIVACY_SECTIONS as unknown as Rec[]).flatMap((sec) => section(sec)),
    '## Leis citadas',
    '',
    ...list(Object.values(LAW_LINKS).map((law) => `[${law.label}](${law.url})`)),
    PRIVACY_DISCLAIMER,
  ];
  return finish(lines);
}

const joinPt = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;

const pct = (value: number) => `${String(value).replace('.', ',')}%`;

export function paymentsHelpMarkdown(): string {
  const f = PAYMENT_FEES;
  const lines = [
    frontMatter({
      title: 'Receber pela Controlapag: taxas e prazos',
      path: '/ajuda/recebimentos',
      description: 'Taxas, prazos e limites do recebimento pelo link da Controlapag: Pix, cartão e boleto.',
      updated: FACTS_CHECKED_AT,
    }),
    '# Receber pela Controlapag: taxas e prazos',
    '',
    `Nos planos pagos, o cliente pode pagar pelo link com Pix, cartão ou boleto, com taxa por pagamento: ${pct(f.pixPercent)} no Pix, ${pct(f.cardPercent)} + ${formatBRL(f.cardFixed)} no cartão e ${formatBRL(f.boletoFixed)} no boleto. A Controlapag não fica com parte do pagamento.`,
    '',
    '## Quanto custa cada forma de pagamento?',
    '',
    ...table(
      ['Forma', 'Taxa por pagamento', 'Quando o dinheiro fica disponível', 'Limites'],
      [
        ['Pix', pct(f.pixPercent), `${f.pixSettlementBusinessDays} dias úteis`, `de ${formatBRL(f.pixMin)} a ${formatBRL(f.pixMax)}`],
        [
          `Cartão (${f.cardBrands.join(' e ')}, à vista)`,
          `${pct(f.cardPercent)} + ${formatBRL(f.cardFixed)}`,
          `${f.cardSettlementDays} dias`,
          '-',
        ],
        ['Boleto (se o profissional ligar)', formatBRL(f.boletoFixed), '-', `de ${formatBRL(f.boletoMin)} a ${formatBRL(f.boletoMax)}`],
      ],
    ),
    '## E se o cliente pagar por fora?',
    '',
    'Em qualquer plano, o cliente pode pagar direto ao profissional (Pix, dinheiro, maquininha). O valor não passa pela Controlapag e não tem taxa: o profissional toca em "Confirmar recebimento" e confirma.',
    '',
    `Conferido em ${FACTS_CHECKED_AT}.`,
  ];
  return finish(lines);
}

/* -------------------------------------------------------------------------- */
/* 404                                                                        */
/* -------------------------------------------------------------------------- */

export const NOT_FOUND_MARKDOWN = `# 404: página não encontrada

Este endereço não existe na Controlapag. Comece por aqui:

- ${SITE_URL}/llms.txt (mapa do site para agentes, com todas as páginas)
- ${SITE_URL}/sitemap.xml
- ${SITE_URL}/guias (guias para autônomo)
- ${SITE_URL}/index.md (a Controlapag em markdown)
`;

/* -------------------------------------------------------------------------- */
/* llms.txt                                                                   */
/* -------------------------------------------------------------------------- */

export function buildLlmsTxt(twinPaths: string[], routeExists: (path: string) => boolean = () => true): string {
  const guides = GUIDE_PAGES as unknown as Rec[];
  const segments = SEGMENT_PAGES as unknown as Rec[];
  const comparisons = COMPARISON_PAGES as unknown as Rec[];
  const mdExample = (path: string) => (twinPaths.includes(path) ? `${SITE_URL}${mdPathFor(path)}` : null);
  const firstGuide = guides[0] ? `/guias/${guides[0].slug}` : null;
  const examples = [mdExample('/'), firstGuide && mdExample(firstGuide), mdExample('/guias')].filter(Boolean);

  const segmentLabels = segments.map((s) => (str(s.label) ?? '').toLowerCase()).filter(Boolean);
  const lines = [
    `# ${LLMS_TITLE}`,
    '',
    `> ${LLMS_SUMMARY}`,
    '',
    `Site: ${SITE_URL}`,
    'Idioma: português do Brasil. Preços em reais.',
    `Revisado em: ${FACTS_CHECKED_AT}`,
    '',
    `Versão em markdown: a home, os guias, as páginas por ramo, as comparações e ${joinPt(
      ['/sobre', '/contato', '/privacidade', '/ajuda/recebimentos'].filter((p) => twinPaths.includes(p)),
    )} respondem em markdown com \`.md\` no fim do endereço (a home em /index.md), ou com \`Accept: text/markdown\` no endereço normal. Exemplos: ${examples.join(', ')}.`,
    '',
    WHEN_TO_USE_HEADING,
    '',
    `- ${WHEN_TO_USE.lead} Ramos com página própria: ${segmentLabels.join(', ')}.`,
    `- ${WHEN_TO_USE.guides}`,
    `- ${WHEN_TO_USE.search}: ${SITE_URL}/buscar`,
    `- ${WHEN_TO_USE.notFor}`,
    `- ${WHEN_TO_USE.legal}`,
    '',
    '### Como um agente deve usar este site',
    '',
    ...agentUsageLines(SITE_URL, segments[0] ? `/para/${segments[0].slug}` : null).map((line) => `- ${line}`),
    '',
    '## O que é grátis e o que é pago',
    '',
    FREE_AND_PAID,
    '',
    '## Planos (preço mensal no site)',
    '',
    plansBlock(),
    '',
    '## Como o cliente paga',
    '',
    paymentBlock(SITE_URL),
    '',
    '## Para o cliente final',
    '',
    FOR_END_CLIENT,
    '',
    '## Perguntas que respondemos',
    '',
    ...guides.map((g) => `- ${guideQuestion(g)}: ${SITE_URL}/guias/${g.slug}`),
    `- Qual é a taxa para receber Pix e cartão pela Controlapag?: ${SITE_URL}/ajuda/recebimentos`,
    '',
    '## Páginas',
    '',
    ...FIXED_PAGES.filter(([path]) => routeExists(path)).map(
      ([path, name, what]) => `- [${name}](${absolute(path)}): ${what}.`,
    ),
    '',
    '## Por ramo',
    '',
    ...segments.map((s) => `- [${str(s.label)}](${SITE_URL}/para/${s.slug}): ${str(s.description) ?? ''}`),
    '',
    '## Comparações',
    '',
    ...comparisons.map((c) => `- [${str(c.h1) ?? str(c.title)}](${SITE_URL}/comparar/${c.slug})`),
    '',
  ];
  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()}\n`;
}

/* -------------------------------------------------------------------------- */
/* Fatos aposentados                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Frases que o site não pode mais dizer (fato falso ou proibido pela marca).
 * O build do `llms.txt`/gêmeos e o `seo.test.mjs` (que varre as páginas)
 * usam esta mesma lista.
 */
export const RETIRED_CLAIMS: { pattern: RegExp; why: string }[] = [
  { pattern: /sem\s+taxas?/i, why: 'o recebimento pelo link tem taxa' },
  { pattern: /100\s*%\s*do\s*valor/i, why: 'o recebimento pelo link tem taxa' },
  { pattern: /teste\s+gr[aá]tis/i, why: 'não existe teste grátis por conta própria' },
  { pattern: /testar\s+gr[aá]tis/i, why: 'não existe teste grátis por conta própria' },
  { pattern: /per[ií]odo\s+de\s+teste/i, why: 'não existe teste grátis por conta própria' },
  { pattern: /p[aá]gina\s+gr[aá]tis/i, why: 'perfil público é pago' },
  { pattern: /Recebi\s+por\s+fora/i, why: 'o botão é "Confirmar recebimento"' },
  { pattern: /Aprovar\s+pagamento/i, why: 'o botão é "Confirmar recebimento"' },
  { pattern: /cobran[cç]a\s+antecipada/i, why: 'não existe cobrança antecipada' },
  { pattern: /Suporte\s+via\s+Chat/i, why: 'suporte só por e-mail' },
  { pattern: /200\s+contratos/i, why: 'limite antigo' },
  { pattern: /\bServidor\b/, why: 'item de plano retirado' },
  { pattern: /R\$\d/, why: 'preço sem espaço: use formatBRL' },
];

export function findRetiredClaims(text: string): string[] {
  return RETIRED_CLAIMS.flatMap(({ pattern, why }) => {
    const match = pattern.exec(text);
    return match ? [`"${match[0]}" (${why})`] : [];
  });
}
